import { chartColorMaps } from "../../utils/colors";
import { pipelineAgeingBuckets } from "../../utils/consts";
import {
  CreateOperationsDashboardBuilderOptions,
  OperationsDashboardBuilderProps,
  OperationalDashboardResponse,
} from "./type";
import { formatValue } from "./utils";

export const createOperationsDashboardBuilder = (
  apiData?: OperationalDashboardResponse,
  options?: CreateOperationsDashboardBuilderOptions,
): OperationsDashboardBuilderProps => {
  
  const formattedRevenue = formatValue(apiData?.data?.business?.revenue?.total_revenue);
  const isBlendedRevenue = apiData?.data?.business?.revenue?.is_blended_revenue ?? false;
  const revenueTooltip = isBlendedRevenue
    ? "Current month revenue is shown in blended mode."
    : undefined;
  const formattedProjectMargin = formatValue(apiData?.data?.business?.project_margin?.total_project_margin);
  const formattedDirectGrossMargin = formatValue(apiData?.data?.business?.direct_gross_margin?.total_direct_gross_margin);
  const formatedCustomerMargin = formatValue(apiData?.data?.business?.customer_margin?.total_customer_margin);
  const ebitda = apiData?.data?.business?.ebitda_margin?.total_ebitda_margin??0;
  const formattedTotalFte = formatValue(apiData?.data?.people_supply_chain?.fte?.total_fte);
  const formattedDeployablePool = formatValue(apiData?.data?.people_supply_chain?.fte?.deployable_pool);
  const formattedSupport = formatValue(apiData?.data?.people_supply_chain?.fte?.support);
  const formattedUtilizationDeployed = formatValue(apiData?.data?.business?.utilization?.total_deployed);
  const formattedUtilizationClientAllocated = formatValue(apiData?.data?.business?.utilization?.total_client);
  const formattedUtilizationBench = formatValue(apiData?.data?.business?.utilization?.total_bench);
  const formattedTotalCapacity = formatValue(apiData?.data?.business?.utilization?.total_capacity);
  const utilizationTotalPoolTooltip = `Total Pool: ${formattedTotalCapacity}`;
  const formattedAttrition = formatValue(apiData?.data?.people_supply_chain?.attrition_rate);
  const formattedClientAllocatedCost = formatValue(apiData?.data?.business?.delivery_cost?.client_allocated_cost);
  const formattedInternalAllocatedCost = formatValue(apiData?.data?.business?.delivery_cost?.internal_cost);
  const formattedBenchCost = formatValue(apiData?.data?.business?.delivery_cost?.bench_cost);
  const formattedBilledUtilizationOffshore = formatValue(apiData?.data?.business?.billed_utilization?.offshore);
  const formattedBilledUtilizationOnsite = formatValue(apiData?.data?.business?.billed_utilization?.onsite);
  const formattedBilledUtilizationOverall = formatValue(apiData?.data?.business?.billed_utilization?.total);
  const formattedTotalBillable = formatValue(apiData?.data?.business?.utilization?.total_billable);
  const formattedDeployedPct = formatValue(apiData?.data?.business?.utilization?.deployed_pct);
  const formattedClientAllocatedPct = formatValue(apiData?.data?.business?.utilization?.client_allocated_pct);
  const formattedBenchPct = formatValue(apiData?.data?.business?.utilization?.bench_pct);
  const formattedBillablePct = formatValue(apiData?.data?.business?.utilization?.billable_pct);
  const formattedBilledUtilizationTotalPct = formatValue(apiData?.data?.business?.billed_utilization?.total_billed_utilization_pct);
  const formattedBilledUtilizationOffshorePct = formatValue(apiData?.data?.business?.billed_utilization?.offshore_pct);
  const formattedBilledUtilizationOnsitePct = formatValue(apiData?.data?.business?.billed_utilization?.onsite_pct);
  const formattedAvgResourceCostOverall = formatValue(apiData?.data?.business?.average_resource_cost?.overall);
  const formattedAvgResourceCostOffshore = formatValue(apiData?.data?.business?.average_resource_cost?.offshore);
  const formattedAvgResourceCostOnsite = formatValue(apiData?.data?.business?.average_resource_cost?.onsite);
  const formattedAverageBillRateOverall = formatValue(apiData?.data?.business?.average_bill_rate?.overall);
  const formattedAverageBillRateOffshore = formatValue(apiData?.data?.business?.average_bill_rate?.offshore);
  const formattedAverageBillRateOnsite = formatValue(apiData?.data?.business?.average_bill_rate?.onsite);
  const formattedClientAllocatedDeliveryCostPct = formatValue(apiData?.data?.business?.delivery_cost?.client_allocated_pct);
  const formattedInternalDeliveryCostPct = formatValue(apiData?.data?.business?.delivery_cost?.internal_pct);
  const formattedBenchDeliveryCostPct = formatValue(apiData?.data?.business?.delivery_cost?.bench_pct);
  const formattedOriginalProjectMargin = formatValue(apiData?.data?.business?.original_project_margin?.total_original_project_margin);
  const formattedOriginalDirectGrossMargin = formatValue(apiData?.data?.business?.original_direct_gross_margin?.total_original_direct_gross_margin);
  const formattedOriginalCustomerMargin = formatValue(apiData?.data?.business?.original_customer_margin?.total_original_customer_margin);
  const formattedOriginalEbitdaMargin = formatValue(apiData?.data?.business?.original_ebitda_margin?.total_original_ebitda_margin);
  const weightedPipelineTotal = formatValue(apiData?.data?.sales?.weighted_pipeline?.total);
  const weightedPipeline = apiData?.data?.sales?.weighted_pipeline;
  const weightedPipelineChartData = weightedPipeline?.stages
    ? Object.entries(weightedPipeline.stages ?? {}).map(([key, value]) => ({
        label: key ? key[0].toUpperCase() + key.slice(1) : "",
        value: Number(value ?? 0),
      }))
    : [];
  const weightedPipelineAccountTypeChartData = apiData?.data?.sales
    ?.weighted_pipeline_account_type
    ? Object.entries(
        apiData?.data?.sales?.weighted_pipeline_account_type ?? {},
      ).map(([key, value]) => ({
        label: key ? key[0].toUpperCase() + key.slice(1) : "",
        value: Number(value ?? 0),
        color: chartColorMaps.weightedPipelineAccountType[key],
      }))
    : [];
  const formattedWinRatio = formatValue(apiData?.data?.sales?.win_ratio?.value);
  const pipelineAgeing = apiData?.data?.sales?.pipeline_ageing;
 
  const pipelineAgeingChartData = pipelineAgeing
    ? pipelineAgeingBuckets.reduce<
        { label: string; value: number; weightedValue: number; color?: string }[]
      >((acc, { key, label }) => {
        const bucket = pipelineAgeing[key];
        if (!bucket) return acc;
        acc.push({
          label,
          value: Number(bucket.count ?? 0),
          weightedValue: Number(bucket.weighted_value ?? 0),
          color: chartColorMaps.pipelineAgeing[label],
        });
        return acc;
      }, [])
    : [];
  const formattedAverageDealSize = formatValue(apiData?.data?.sales?.average_deal_size?.value);
  const {
    onRevenueClick,
    onProjectMarginClick,
    onCustomerMarginClick,
    onDgmClick,
    onEbitdaClick,
    onRevenueCostAnalysisClick,
    onUtilizationClick,
    onSalesPipelineHealthClick,
    onWinRatioClick,
  } = options ?? {};

  return {
    sections: [
      {
        id: "business",
        title: "Business",
        columns: {
          xs: 1,
          sm: 1,
          md: 2,
          lg: 4,
          xl: 4,
        },
        metrics: [
          {
            id: "revenue",
            title: "Revenue",
            value: `${formattedRevenue}`,
            unit: "$",
            status: "success",
            ...(isBlendedRevenue && { infoTooltip: revenueTooltip }),
            forecast: {
              value: "$0m",
              label: "forecasted",
            },
            comparison: {
              value: "$0",
              label: "vs budget",
              type: "vs_budget",
            },
            onClick: onRevenueClick,
          },
          {
            id: "project-margin",
            title: "Project Margin",
            value: `${formattedProjectMargin}%`,
            subValue: `(${formattedOriginalProjectMargin})`,
            status: "success",
            forecast: {
              value: "0%m",
              label: "forecasted",
            },
            comparison: {
              value: "+0 bps",
              label: "vs budget",
              type: "vs_budget",
            },
            onClick: onProjectMarginClick,
          },
          {
            id: "customer-margin",
            title: "Customer Margin",
            value: `${formatedCustomerMargin}%`,
            subValue: `(${formattedOriginalCustomerMargin})`,
            status: "success",
            forecast: {
              value: "0%",
              label: "forecasted",
            },
            comparison: {
              value: "+0 bps",
              label: "vs budget",
              type: "vs_budget",
            },
            onClick: onCustomerMarginClick,
          },
          {
            id: "d-gm",
            title: "D-GM%",
            value: `${formattedDirectGrossMargin}%`,
            subValue: `(${formattedOriginalDirectGrossMargin})`,
            status: "success",
            forecast: {
              value: "0%",
              label: "forecasted",
            },
            comparison: {
              value: "+0 bps",
              label: "vs budget",
              type: "vs_budget",
            },
            onClick: onDgmClick,
          },
          {
            id: "ebitda-bu-gm",
            title: "EBITDA%/BU GM",
            value: `${ebitda}%`,
            subValue: `(${formattedOriginalEbitdaMargin})`,
            status: "success",
            forecast: {
              value: "0%",
              label: "forecasted",
            },
            comparison: {
              value: "+0 bps",
              label: "vs budget",
              type: "vs_budget",
            },
            onClick: onEbitdaClick,
          },
          {
            id: "total-unbilled-revenue",
            title: "Total Unbilled Revenue",
            value: "0m",
            status: "error",
            unit: "$",
            cardType: "time-based",
            timeframe: "0 days",
            subTimeframe: "Avg. UR Days",
          },
          {
            id: "ar",
            title: "AR",
            value: "0m",
            status: "error",
            unit: "$",
            cardType: "time-based",
            timeframe: "0 days",
            subTimeframe: "Avg. AR Days",
          },
          {
            id: "dso",
            title: "DSO",
            value: "0",
            status: "error",
            cardType: "gauge",
            comparison: {
              value: "",
              label: "+ 0 vs target",
              type: "vs_target",
            },
          },
          {
            id: "delivery-cost",
            title: "Delivery Cost",
            subtitle: "as % of revenue",
            value: "",
            status: "success",
            comparison: {
              value: "",
              label: "",
              type: "vs_previous",
            },
            cardType: "breakdown",
            breakdown: [
              { label: "Client allocated", value: `${formattedClientAllocatedCost}`, percentage: `${formattedClientAllocatedDeliveryCostPct} %` },
              { label: "Internal + Invest", value: `${formattedInternalAllocatedCost}`, percentage: `${formattedInternalDeliveryCostPct} %` },
              { label: "Bench", value: `${formattedBenchCost}`, percentage: `${formattedBenchDeliveryCostPct} %` },
            ],
            onClick: onRevenueCostAnalysisClick,
          },
          {
            id: "utilization",
            title: "Utilization",
            value: "vs budget",
            status: "success",
            cardType: "breakdown",
            infoTooltip: utilizationTotalPoolTooltip,
            comparison: {
              value: "",
              label: "",
              type: "vs_previous",
            },
            breakdown: [
              { label: "Deployed", value: `${formattedUtilizationDeployed}`, percentage: `${formattedDeployedPct} %` },
              { label: "Client Allocated", value: `${formattedUtilizationClientAllocated}`, percentage: `${formattedClientAllocatedPct} %`},
              { label: "Client Billable", value: `${formattedTotalBillable}`, percentage: `${formattedBillablePct} %` },
              { label: "Bench", value: `${formattedUtilizationBench}`, percentage: `${formattedBenchPct} %` },
            ],
            onClick: onUtilizationClick,
          },
          {
            id: "billed-utilization",
            title: "Billed Utilization",
            value: "vs budget",
            status: "success",
            cardType: "breakdown",
            comparison: {
              value: "",
              label: "",
              type: "vs_previous",
            },
            breakdown: [
              { label: "Overall", value: `${formattedBilledUtilizationOverall}`, percentage: `${formattedBilledUtilizationTotalPct} %` },
              { label: "Offshore", value: `${formattedBilledUtilizationOffshore}`, percentage: `${formattedBilledUtilizationOffshorePct} %` },
              { label: "Onsite", value: `${formattedBilledUtilizationOnsite}`, percentage: `${formattedBilledUtilizationOnsitePct} %` },
            ],
            onClick: onUtilizationClick,
          },
          {
            id: "avg-bill-rate",
            title: "Avg. Bill Rate ($)",
            value: "vs budget",
            status: "success",
            cardType: "breakdown",
            comparison: {
              value: "",
              label: "",
              type: "vs_previous",
            },
            breakdown: [
              { label: "Overall", value: `${formattedAverageBillRateOverall}` },
              { label: "Offshore", value: `${formattedAverageBillRateOffshore}` },
              { label: "Onsite", value: `${formattedAverageBillRateOnsite}` },
            ],
            onClick: onRevenueCostAnalysisClick,
          },
          {
            id: "arc-sr",
            title: "ARC ($k)",
            value: "vs budget",
            status: "success",
            cardType: "breakdown",
            comparison: {
              value: "",
              label: "",
              type: "vs_previous",
            },
            breakdown: [
              { label: "Overall", value: `${formattedAvgResourceCostOverall}` },
              { label: "Offshore", value: `${formattedAvgResourceCostOffshore}`},
              { label: "Onsite", value: `${formattedAvgResourceCostOnsite}` },
            ],
            onClick: onRevenueCostAnalysisClick,
          },
          {
            id: "bid-vs-del-variance",
            title: "Bid vs Did Variance",
            value: "0%",
          },
        ],
      },
      {
        id: "sales",
        title: "Sales",
        columns: {
          xs: 1,
          sm: 1,
          md: 2,
          lg: 4,
          xl: 4,
        },
        metrics: [
          {
            id: "wt-pipeline",
            title: "Wt. Pipeline",
            unit: "$",
            value: `${weightedPipelineTotal}`,
            cardType: "chart",
            chart: {
              type: "bar",
              variant: "horizontal",
              tooltipValuePrefix: "$",
              data: weightedPipelineChartData,
            },
          },
          {
            id: "wt-pipeline-2",
            title: "Wt. Pipeline",
            value: "",
            comparison: {
              value: "vs QR target",
              label: "",
              type: "vs_target",
            },
            chart: {
              type: "bar",
              variant: "vertical",
              tooltipValuePrefix: "$",
              data: weightedPipelineAccountTypeChartData,
            },
            onClick: onSalesPipelineHealthClick,
          },
          {
            id: "pipeline-coverage",
            title: "Pipeline Coverage",
            value: "0%",
            status: "error",
            cardType: "gauge",
            comparison: {
              value: "",
              label: "vs QB target",
              type: "vs_target",
            },
          },
          {
            id: "pipeline-ageing",
            title: "Pipeline Ageing",
            subtitle: "in days",
            value: "",
            cardType: "chart",
            chart: {
              type: "bar",
              variant: "vertical",
              tooltipValueLabel: "Count",
              tooltipSecondaryValueKey: "weightedValue",
              tooltipSecondaryValueLabel: "Weighted Value",
              tooltipSecondaryValuePrefix: "$",
              data: pipelineAgeingChartData,
            },
            onClick: onSalesPipelineHealthClick,
          },
          {
            id: "orderbook",
            title: "Orderbook",
            value: "0m",
            unit: "$",
            status: "error",
            cardType: "time-based",
            timeframe: "+$0m",
            subTimeframe: "vs target",
            chart: {
              type: "bar",
              data: [],
            },
          },
          {
            id: "orderbook-vs-target",
            title: "Orderbook",
            subtitle: "vs target",
            value: "",
            chart: {
              type: "bar",
              data: [],
            },
          },
          {
            id: "avg-sold-gm",
            title: "Average Sold GM%",
            value: "0%",
            status: "error",
            cardType: "time-based",
            timeframe: "+0 bps",
            subTimeframe: "vs budget",
          },
          {
            id: "avg-deal-size",
            title: "Average Deal Size",
            value: `${formattedAverageDealSize}`,
            unit: "$",
            status: "success",
            cardType: "time-based",
            timeframe: "+$0 m",
            subTimeframe: "vs target",
          },
          {
            id: "win-ratio",
            title: "Win Ratio%",
            value: `${formattedWinRatio}%`,
            cardType: "time-based",
            timeframe: "+0 bps",
            subTimeframe: "vs target",
            status: "success",
            onClick: onWinRatioClick,
          },
        ],
      },
      {
        id: "people-supply",
        title: "People Supply Chain",
        columns: {
          xs: 1,
          sm: 1,
          md: 2,
          lg: 4,
          xl: 4,
        },
        metrics: [
          {
            id: "total-ftes",
            title: "Total FTEs",
            value: formattedTotalFte,
            cardType: "split",
            splitValues: [
              { label: "Deployable Pool", value: formattedDeployablePool },
              { label: "Support", value: formattedSupport },
            ],
          },
          {
            id: "lead-time",
            title: "Lead Time to Fulfill",
            value: "",
            cardType: "multi-metric",
            multiMetrics: [
              { label: "Offshore", value: "0", unit: " Days" },
              { label: "Onsite", value: "0", unit: " Days" },
            ],
            comparison: {
              value: "+0",
              label: "vs target",
              type: "vs_target",
            },
            status: "error",
          },
          {
            id: "internal-fullfilment",
            title: "Internal Fulfilment%",
            value: "",
            cardType: "multi-metric",
            multiMetrics: [
              { label: "Offshore", value: "0%", unit: "" },
              { label: "Onsite", value: "0%", unit: "" },
            ],
            comparison: {
              value: "+0bps",
              label: "vs target",
              type: "vs_target",
            },
            status: "error",
          },
          {
            id: "attrition",
            title: "Attrition",
            value: `${formattedAttrition}%`,
          },
        ],
      },
    ],
  };
};

// Keep the original static export for backward compatibility
export const operationsDashboardBuilder = createOperationsDashboardBuilder();
